import { sessionDestination } from '../shared/session/session-destination';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation, useParams } from 'react-router';
import { useSession } from '../shared/session/use-session';
import { Banner } from '../shared/ui/banner';
import { Button } from '../shared/ui/button';
import { LoadingState } from '../shared/ui/loading-state';
import { NotFoundPage } from './not-found-page';

const SessionError = ({ retry }: { retry: () => Promise<unknown> }) => {
  const { t } = useTranslation();
  return (
    <div className="max-w-md space-y-4">
      <Banner tone="error">{t('errors.network')}</Banner>
      <Button type="button" variant="secondary" onClick={() => void retry()}>
        {t('common.retry')}
      </Button>
    </div>
  );
};

const intendedPath = ({ pathname, search, hash }: ReturnType<typeof useLocation>) =>
  `${pathname}${search}${hash}`;

export const RequireSession = () => {
  const { user, isPending, isError, refetch } = useSession();
  const { locale } = useParams();
  const location = useLocation();
  if (isPending) return <LoadingState rows={4} />;
  if (isError && !user) return <SessionError retry={refetch} />;
  if (!user) {
    const next = encodeURIComponent(intendedPath(location));
    return <Navigate to={`/${locale}/sign-in?next=${next}`} replace />;
  }
  return <Outlet />;
};

export const RequireAdmin = () => {
  const { user, isPending, isError, refetch } = useSession();
  const { locale } = useParams();
  const location = useLocation();
  if (isPending) return <LoadingState rows={4} />;
  if (isError && !user) return <SessionError retry={refetch} />;
  if (!user) {
    const next = encodeURIComponent(intendedPath(location));
    return <Navigate to={`/${locale}/sign-in?next=${next}`} replace />;
  }
  if (user.role !== 'admin') return <NotFoundPage />;
  return <Outlet />;
};

export const PublicOnly = () => {
  const { user, isError, refetch } = useSession();
  const { locale } = useParams();
  if (user) return <Navigate to={sessionDestination(user, locale)} replace />;
  return (
    <>
      {isError ? <SessionError retry={refetch} /> : null}
      <Outlet />
    </>
  );
};
